import React from "react";
import { Box, Chip, IconButton, Link, Typography } from "@mui/material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import TrainIcon from "@mui/icons-material/Train";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import KeyboardReturnIcon from "@mui/icons-material/KeyboardReturn";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

import { Location } from "../../../@types/DTO/Locations";
import { buildOpeningsSummary } from "./openingsSummary";
import {
  formatLocationAddress,
  isTruthyLocationFlag,
} from "./locationFormatters";

export interface LocationDetailPanelProps {
  location: Location;
  anchorDate?: Date | null;
  onOpenDetails: () => void;
  className?: string;
}

const LocationDetailPanel: React.FC<LocationDetailPanelProps> = ({
  location,
  anchorDate,
  onOpenDetails,
  className,
}) => {
  const { t } = useTranslation();

  const isAirport = isTruthyLocationFlag(location.isAirport);
  const isRailway = isTruthyLocationFlag(location.isRailway);
  const showPickupBadge = isTruthyLocationFlag(location.pickUpOutOfHours);
  const showReturnBadge = isTruthyLocationFlag(location.dropOffOutOfHours);

  // BookingForm's previous per-option icon.
  const typeIconNode = isAirport ? (
    <FlightTakeoffIcon htmlColor="blue" />
  ) : isRailway ? (
    <TrainIcon htmlColor="green" />
  ) : (
    <LocationCityIcon color="primary" />
  );

  const hoursText = buildOpeningsSummary(location, { t, anchorDate });

  return (
    <Box
      className={className}
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        minWidth: 280,
        maxWidth: 380,
        borderRadius: 1,
      }}
      data-testid="location-detail-panel"
    >
      {/* Row 1: type icon + badges */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        {typeIconNode}
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "nowrap",
          }}
        >
          {showPickupBadge && (
            <Chip
              size="small"
              icon={<VpnKeyIcon />}
              label={t("Ritiro 24 ore")}
              variant="outlined"
              color="primary"
              sx={{ borderRadius: 999 }}
            />
          )}
          {showReturnBadge && (
            <Chip
              size="small"
              icon={<KeyboardReturnIcon />}
              label={t("Restituzione 24 ore")}
              variant="outlined"
              color="primary"
              sx={{ borderRadius: 999 }}
            />
          )}
        </Box>
      </Box>

      {/* Row 2: name */}
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
        {location.locationName}
      </Typography>

      {/* Row 3: address */}
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {formatLocationAddress(location)}
      </Typography>

      {/* Row 4: opening hours */}
      {hoursText && (
        <Box sx={{ mt: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t("Opening hours")}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {hoursText}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
        <IconButton
          size="small"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails();
          }}
          aria-label={t("Station details")}
          sx={{ p: 0 }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
        <Link
          component="button"
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails();
          }}
          underline="always"
          sx={{
            color: "text.primary",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          {t("Station details")}
        </Link>
      </Box>
    </Box>
  );
};

export default LocationDetailPanel;
